import React, { Fragment } from "react";

import { AdministrationCard } from "@common/components/AdministrationCard/AdministrationCard";
import Button from "@common/components/Button/Button";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { CreditCard, Customer, mapCreditCardBrandToLogo } from "@common/models/billing";
import { dateStringForMappingFiles } from "@common/models/helpers/time/creationTime";
import { vars } from "@common/styles";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import identifiers from "@common/util/identifiers.json";

import * as Styled from "./BillingCards.styles";

interface CreditCardDetailsCardProps {
  customer: Customer;
  upcomingAmount?: number;
  creditCard?: CreditCard;
  updateCreditCard: () => Promise<void>;
}

export default function CreditCardDetailsCard({
  customer,
  upcomingAmount,
  creditCard,
  updateCreditCard,
}: CreditCardDetailsCardProps): JSX.Element {
  return (
    <AdministrationCard
      title="Credit card details"
      subtitle="All payments are secure and encrypted by Stripe."
      topLeftAccessory={
        <Button
          size="small"
          onClick={() => updateCreditCard()}
          testId={identifiers["billing.button.update.card"]}
        >
          Update credit card
        </Button>
      }
      testId={identifiers["billing.header.card.details"]}
    >
      {cardDisplay(customer, upcomingAmount, creditCard)}
    </AdministrationCard>
  );
}

const cardDisplay = (customer: Customer, upcomingAmount?: number, creditCard?: CreditCard) => {
  if (!creditCard)
    return (
      <Flex
        $alignItems={"stretch"}
        $justifyContent={"space-between"}
      >
        <p style={{ color: vars.colors.red }}>Credit card missing</p>
        <Styled.Flex $alignItems="flex-start">{getNextInvoice(customer, upcomingAmount)}</Styled.Flex>
      </Flex>
    );

  return (
    <Flex>
      <Styled.Flex $alignItems="center">
        <Styled.CardImage src={mapCreditCardBrandToLogo(creditCard.brand)} />
        <Flex $alignItems="center">
          <Styled.Dots />
          <Styled.Dots />
          <Styled.Dots />
          <p>{creditCard.last4}</p>
        </Flex>
      </Styled.Flex>
      {getNextInvoice(customer, upcomingAmount)}
    </Flex>
  );
};

const getNextInvoice = (customer: Customer, upcomingAmount?: number) => {
  if (!customer.subscription) return <Fragment />;

  if (upcomingAmount !== undefined)
    return (
      <Paragraph
        color={vars.colors.grey30}
        fontWeight={500}
        data-testid={identifiers["billing.info.next.invoice"]}
      >
        {`Next invoice is $${
          upcomingAmount > 0 ? upcomingAmount.toString().slice(0, -2) : 0
        } on ${dateStringForMappingFiles(new Date(customer.subscription.current_period_end))}`}
      </Paragraph>
    );
};
